import { useEffect, useState } from "react";
import { TaskAPI, PlaylistsAPI } from "../api/api";
import { useNavigate } from "react-router-dom";
import "../pages/Tasks/Tasks.css";

const repeatOptions = ["Once", "Daily", "Weekly"];
const weekDays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export default function TaskForm({ onSuccess, editingTask }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [repeatability, setRepeatability] = useState("Once");
  const [startTime, setStartTime] = useState("");
  const [dailyTime, setDailyTime] = useState("");
  const [weeklyDay, setWeeklyDay] = useState(0);
  const [weeklyTime, setWeeklyTime] = useState("");
  const [activePlaylist, setActivePlaylist] = useState("");
  const [playlists, setPlaylists] = useState([]);
  const [loadingPlaylists, setLoadingPlaylists] = useState(true);

  useEffect(() => {
    const fetchPlaylists = async () => {
      try {
        const res = await PlaylistsAPI.getPlaylists();
        setPlaylists(res.data);
      } catch (err) {
        console.error("Failed to fetch playlists:", err);
      } finally {
        setLoadingPlaylists(false);
      }
    };
    fetchPlaylists();
  }, []);

  const utcToLocalDateTimeInput = (utcString) => {
    if (!utcString) return "";
    const d = new Date(utcString + "Z");
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const utcTimeToLocalInput = (utcTimeString) => {
    if (!utcTimeString) return "";
    const [h, m] = utcTimeString.split(":").map(Number);

    const d = new Date();
    d.setUTCHours(h, m, 0, 0);

    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");

    return `${hours}:${minutes}`;
  };

  useEffect(() => {
    if (editingTask) {
      setName(editingTask.name);
      setDescription(editingTask.description);
      setRepeatability(editingTask.repeatability);
      setStartTime(editingTask.startTime ? utcToLocalDateTimeInput(editingTask.startTime) : "");
      setDailyTime(editingTask.dailyTime ? utcTimeToLocalInput(editingTask.dailyTime) : "");
      setWeeklyDay(editingTask.weeklyDay || 0);
      setWeeklyTime(editingTask.weeklyTime ? utcTimeToLocalInput(editingTask.weeklyTime) : "");
      setActivePlaylist(editingTask.activePlaylist || "");
    } else {
      setName("");
      setDescription("");
      setRepeatability("Once");
      setStartTime("");
      setDailyTime("");
      setWeeklyDay(0);
      setWeeklyTime("");
      setActivePlaylist("");
    }
  }, [editingTask]);

  const handleCancel = () => {
    navigate("/tasks");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const localDateTimeToISOString = (localDateTime) => {
      if (!localDateTime) return null;

      const [datePart, timePart] = localDateTime.split("T");
      const [year, month, day] = datePart.split("-").map(Number);
      const [hours, minutes] = timePart.split(":").map(Number);

      const date = new Date(year, month - 1, day, hours, minutes, 0, 0);
      return date.toISOString();
    };

    const payload = {
      name,
      description,
      repeatability,
      activePlaylist,
      startTime: repeatability === "Once" ? localDateTimeToISOString(startTime) : null,
      dailyTime: repeatability === "Daily"
        ? (() => {
          const [hours, minutes] = dailyTime.split(":").map(Number);
          const date = new Date();
          date.setHours(hours, minutes, 0, 0);
          return `${date.getUTCHours().toString().padStart(2, "0")}:${date
            .getUTCMinutes()
            .toString()
            .padStart(2, "0")}:00`;
        })()
        : null,
      weeklyTime:
        repeatability === "Weekly" && weeklyTime
          ? (() => {
            const [hours, minutes] = weeklyTime.split(":").map(Number);
            const date = new Date();
            date.setHours(hours, minutes, 0, 0);
            return date.toISOString().substr(11, 8);
          })()
          : null,
      weeklyDay: repeatability === "Weekly" ? weeklyDay : null,
    };

    try {
      if (editingTask) {
        await TaskAPI.update(editingTask.id, payload);
      } else {
        await TaskAPI.create(payload);
      }
      onSuccess();
    } catch (err) {
      console.error("Failed to save task:", err);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="task-form">
      <div className="name-disc">
        <div className="fild">
          <div>Name<span className="required">*</span></div>
          <input
            name="name"
            type="text"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div className="fild">
          <div>Description</div>
          <input
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>

      <div className="repeat-start">
        <div className="fild">
          <div>Repeatability<span className="required">*</span></div>
          <select
            value={repeatability}
            onChange={(e) => setRepeatability(e.target.value)}
            required
          >
            {repeatOptions.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
        <div className="fild">
          <div>Start At<span className="required">*</span></div>

          {repeatability === "Once" && (
            <input
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          )}

          {repeatability === "Daily" && (
            <input
              type="time"
              value={dailyTime}
              onChange={(e) => setDailyTime(e.target.value)}
              className="time"
            />
          )}

          {repeatability === "Weekly" && (
            <div className="weekly-time">
              <select
                name="day"
                value={weeklyDay}
                onChange={(e) => setWeeklyDay(Number(e.target.value))}
                className="time"
              >
                {weekDays.map((day, i) => (
                  <option key={day} value={i}>{day}</option>
                ))}
              </select>
              <input
                type="time"
                value={weeklyTime}
                onChange={(e) => setWeeklyTime(e.target.value)}
                className="time"
              />
            </div>
          )}
        </div>
      </div>
      <div className="playlist">
        <div className="fild">
          <div>Active Playlist<span className="required">*</span></div>
          {loadingPlaylists ? (
            <div>Loading playlists...</div>
          ) : (
            <select
              value={activePlaylist}
              onChange={(e) => setActivePlaylist(e.target.value)}
              required
            >
              <option value="">Select playlist...</option>
              {playlists.map((playlist, i) => (
                <option key={i} value={playlist.name}>
                  {playlist.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
      <div className="form-btn">
        <button type="submit" className="submit-btn">
          {editingTask ? "UPDATE" : "CREATE"} TASK
        </button>
        <button onClick={handleCancel} className="cancel-btn">
          CANCEL
        </button>
      </div>
    </form>
  );
}
